import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, shareReplay, Subject, BehaviorSubject, interval, firstValueFrom } from 'rxjs';
import { Constants } from '../constants';
import { HttpClient } from '@angular/common/http';
import { AllNewsResult, ConfigurationModel, VeNewsFeed, AuthorizedToken } from '../typings';
import { AuthService } from './auth.service';

@Injectable()
export class SharedService {

  private videoEndedSource = new Subject<void>();
  videoEnded$ = this.videoEndedSource.asObservable();
  private configurationObs$?: Observable<ConfigurationModel>;
  public blobUrl: string;
  private youtubeApi: string;
  private readonly TOKEN_CONFIG_REFRESH_INTERVAL = 30000;

  private readonly tokenConfigSubject: BehaviorSubject<AuthorizedToken | null>;
  public readonly tokenConfig$: Observable<AuthorizedToken | null>;

  constructor(private http: HttpClient, private injector: Injector, private authService: AuthService) {
    const stored = localStorage.getItem(Constants.LocalStorage.TokenConfig);
    this.tokenConfigSubject = new BehaviorSubject<AuthorizedToken | null>(
      stored ? JSON.parse(stored) as AuthorizedToken : null
    );
    this.tokenConfig$ = this.tokenConfigSubject.asObservable();

    this.getConfiguration().subscribe(config => {
      this.blobUrl = config.blobUrl;
      this.youtubeApi = config.youtubeApi;
    });

    interval(this.TOKEN_CONFIG_REFRESH_INTERVAL).subscribe(() => {
      const tokenConfig = this.tokenConfigSubject.getValue();
      if (tokenConfig?.encryptedToken) {
        this.authService.refreshTokenConfig(tokenConfig.encryptedToken).subscribe({
          next: refreshed => { if (refreshed) this.setTokenConfig(refreshed); },
          error: () => {}
        });
      }
    });
  }

  /**
   * Navigate to the correct Home or Local route based on displayMode.
   * @param type 'home' | 'local'
   */
  public navigateByDisplayMode(type: 'home' | 'local') {
    const displayMode = this.getTokenConfig()?.displayMode;
    let route: string;
    if (type === 'home') {
      route = displayMode === Constants.DisplayMode.BigPicture ? Constants.RedirectUrl.BigHome : Constants.RedirectUrl.Home;
    } else {
      route = displayMode === Constants.DisplayMode.BigPicture ? Constants.RedirectUrl.BigLocalNews : Constants.RedirectUrl.LocalNews;
    }
    this.navigate([route]);
  }

  /**
   * Navigate to the correct initial route based on displayMode and available news.
   * Centralise la logique de redirection post-authentification.
   */
  public navigateAfterAuth(news: AllNewsResult | null): void {
    const tokenConfig = this.getTokenConfig();
    if (!tokenConfig || !news) return;
    const isBig = tokenConfig.displayMode === Constants.DisplayMode.BigPicture;
    if (news?.localNews && news.localNews.length > 0) {
      this.navigate([isBig ? Constants.RedirectUrl.BigLocalNews : Constants.RedirectUrl.LocalNews]);
    } else if (news?.internalNews && news.internalNews.length > 0) {
      this.navigate([isBig ? Constants.RedirectUrl.BigHome : Constants.RedirectUrl.Home]);
    }
  }

  /**
   * Returns the encryptedToken from TokenConfig if available, otherwise null.
   */
  public getEncryptedToken(): string | null {
    const tokenConfig = this.getTokenConfig();
    return tokenConfig && tokenConfig.encryptedToken ? tokenConfig.encryptedToken : null;
  }

  emitVideoEnded(): void {
    this.videoEndedSource.next();
  }


  /**
   * Deprecated: setToken should no longer be used. Use setTokenConfig with a valid tokenConfig instead.
   */
  public setToken(token: string) {
    // Deprecated: do nothing or optionally log a warning
    // console.warn('setToken is deprecated. Use setTokenConfig instead.');
  }

  /**
   * Returns the encryptedToken from tokenConfig if available. If not, attempts to fetch it using the plain token,
   * updates tokenConfig, removes ComEntToken, and returns the encryptedToken. Returns a Promise<string|null>.
   */
  public async getToken(): Promise<string | null> {
    const tokenConfig = this.getTokenConfig();
    if (tokenConfig && tokenConfig.encryptedToken) {
      return tokenConfig.encryptedToken;
    }
    // Fallback: try to get plain token and fetch tokenConfig
    const plainToken = localStorage.getItem(Constants.LocalStorage.ComEntToken);
    if (!plainToken || !this.authService) {
      return null;
    }
    try {
      const result = await firstValueFrom(this.authService.getTokenConfig(plainToken));
      if (result && result.encryptedToken) {
        this.setTokenConfig(result);
        localStorage.removeItem(Constants.LocalStorage.ComEntToken);
        return result.encryptedToken;
      }
      return null;
    } catch {
      return null;
    }
  }

  public setTokenConfig(tokenConfig: AuthorizedToken) {
    if (tokenConfig != null) {
      localStorage.setItem(Constants.LocalStorage.TokenConfig, JSON.stringify(tokenConfig));
      this.tokenConfigSubject.next(tokenConfig);
    }
  }

  /**
   * Returns an observable for TokenConfig changes.
   * @deprecated Use tokenConfig$ directly.
   */
  public getTokenConfigSubscription(): Observable<AuthorizedToken | null> {
    return this.tokenConfig$;
  }

  /**
   * Returns the current TokenConfig synchronously.
   */
  public getTokenConfig(): AuthorizedToken | null {
    return this.tokenConfigSubject.getValue();
  }

  public navigate(_options: string[]) {
    const router = this.injector.get(Router);
    router.navigate(_options);
  }

  public isYoutubeVideo(currentNews: VeNewsFeed) {
    if (currentNews?.videoUrl?.startsWith(Constants.BaseUrl.Youtube_URL)) {
      return true;
    }
    return false;
  }


  public isStreamVideo(currentNews: VeNewsFeed) {
    if (currentNews?.videoUrl) {
      return this.isInBlob(currentNews.videoUrl);
    }
    return false;
  }

  public isVideo(currentNews: VeNewsFeed) {
    if (this.isYoutubeVideo(currentNews) || this.isStreamVideo(currentNews)) {
      return true;
    }
    return false;
  }

  public buildUrlBlob(imageName:string|undefined, folder:string): string {
      if (!imageName) {
          return ''; 
      }
      return `${this.blobUrl}/${folder}${imageName}`;
  }

   public getConfiguration(): Observable<ConfigurationModel> {
        if (!this.configurationObs$) {
            this.configurationObs$ = this.http
                .get<ConfigurationModel>(`${Constants.Api.Config}`)
                .pipe(
                    shareReplay(1)
                );
        }
        return this.configurationObs$;
    }

 public isInBlob(link: string) : boolean {
    if (link.startsWith(Constants.BaseUrl.Blob_DEV_URL) ||
      link.startsWith(Constants.BaseUrl.Blob_UAT_URL) ||
      link.startsWith(Constants.BaseUrl.Blob_PRE_URL) ||
      link.startsWith(Constants.BaseUrl.Blob_PRD_URL) ) {
      return true;
    }
    return false;
  }

  public isScriptLoaded() {
    return document.querySelector('script[src="' + this.youtubeApi + '"]') != null;
  }

}
